import { FC, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
	TGetTikTokAttribute,
	TGetTikTokAttributeValue,
	TTikTokVerificationSelectedValues,
} from "../../../api/api.types";

import Arrow from "../../assets/images/arrow.svg";

type DropdownProps = {
	attribute: TGetTikTokAttribute;
	setSelectedValues: React.Dispatch<
		React.SetStateAction<TTikTokVerificationSelectedValues>
	>;
	selectedValues: TTikTokVerificationSelectedValues;
	errorFields: string[];
	setErrorFields: React.Dispatch<React.SetStateAction<string[]>>;
};

const Dropdown: FC<DropdownProps> = ({
	attribute,
	setSelectedValues,
	selectedValues,
	errorFields,
	setErrorFields,
}) => {
	const [customValue, setCustomValue] = useState<string>("");
	const [isOpen, setIsOpen] = useState(false);

	const dropdownRef = useRef<HTMLDivElement>(null);

	const intl = useIntl();

	const handleSelectValue = (value: TGetTikTokAttributeValue) => {
		setSelectedValues((prev) => ({
			...prev,
			[attribute.id]: value,
		}));
		setIsOpen(false);

		if (value.id !== "custom") {
			setCustomValue("");
		}

		if (errorFields.includes(attribute.id)) {
			setErrorFields((prev) =>
				prev.filter((item) => item !== attribute.id)
			);
		}
	};

	const handleApplyCustom = () => {
		if (customValue.trim() === "") {
			return;
		}

		setSelectedValues((prev) => ({
			...prev,
			[attribute.id]: {
				id: "custom",
				name: customValue,
			},
		}));
		setIsOpen(false);

		if (errorFields.includes(attribute.id)) {
			setErrorFields((prev) =>
				prev.filter((item) => item !== attribute.id)
			);
		}
	};

	useEffect(() => {
		if (selectedValues[attribute.id]?.id === "custom") {
			setCustomValue(selectedValues[attribute.id].name);
		}
	}, [selectedValues, attribute.id]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	return (
		<div ref={dropdownRef} className="tiktok-attribute-dropdown__wrapper">
			<div className="tiktok-attribute-dropdown__label">
				<span>
					{attribute.name} <span style={{ color: "red" }}>*</span>
				</span>
				<div
					onClick={() => setIsOpen(!isOpen)}
					className={`tiktok-attribute-dropdown__button ${isOpen ? "opened" : ""} ${
						errorFields.includes(attribute.id) ? "errored" : ""
					}`}
				>
					<span>{selectedValues[attribute.id]?.name}</span>
					<img src={Arrow} alt="" />
				</div>
			</div>
			{isOpen && (
				<div className="tiktok-attribute-dropdown__content">
					<div className="tiktok-attribute-dropdown__content-inner">
						<div className="tiktok-attribute-dropdown__items">
							{attribute.values.map((value) => (
								<div
									onClick={() => handleSelectValue(value)}
									className="tiktok-attribute-dropdown__item"
									key={value.id}
								>
									{value.name}
								</div>
							))}
						</div>
						{attribute.is_customizable && (
							<div className="tiktok-attribute-dropdown__custom">
								<input
									onChange={(e) =>
										setCustomValue(e.target.value)
									}
									className="tiktok-attribute-dropdown__input"
									placeholder={intl.formatMessage({
										id: "customValue",
									})}
									value={customValue}
								/>
								<button
									onClick={handleApplyCustom}
									className="tiktok-attribute-dropdown__custom-apply"
								>
									<FormattedMessage id="apply" />
								</button>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default Dropdown;
